import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Plus, Pencil, Trash2, Filter, Search, ChevronDown, ChevronUp, Loader2, Tag, Zap, Palette } from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
} from '@jira-clone/ui'
import { useUnitMasters, useUnitMasterMutations } from '../../shared/hooks/useUnitMasters'
import { getApiErrorMessage } from '../../shared/utils/apiError'

interface DraftState {
  id?: string
  name: string
  shortCode: string
  displayName: string
  isActive: boolean
}

const EMPTY_DRAFT: DraftState = {
  name: '',
  shortCode: '',
  displayName: '',
  isActive: true,
}

export default function UnitMasterAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'true' | 'false'>(searchParams.get('isActive') as any || 'all')

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setSearchParams(value ? { search: value } : {})
  }

  const handleIsActiveChange = (value: 'all' | 'true' | 'false') => {
    setIsActiveFilter(value)
    setSearchParams(value === 'all' ? {} : { isActive: value })
  }

  const { data: unitMasters = [], isLoading } = useUnitMasters({
    isActive: isActiveFilter === 'all' ? undefined : isActiveFilter === 'true',
    search: search || undefined,
  })
  
  const { create, update, setStatus, remove } = useUnitMasterMutations()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const openCreate = () => {
    setDraft(EMPTY_DRAFT)
    setDialogOpen(true)
  }

  const openEdit = (unit: any) => {
    setDraft({
      id: unit.id,
      name: unit.name,
      shortCode: unit.shortCode,
      displayName: unit.displayName,
      isActive: unit.isActive,
    })
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!draft.name.trim()) {
      toast.error('Name is required.')
      return
    }
    if (!draft.shortCode.trim()) {
      toast.error('Short code is required.')
      return
    }
    if (!draft.displayName.trim()) {
      toast.error('Display name is required.')
      return
    }
    try {
      const payload = {
        name: draft.name.trim(),
        shortCode: draft.shortCode.trim().toUpperCase(),
        displayName: draft.displayName.trim(),
        isActive: draft.isActive,
      }
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, payload })
        toast.success('Unit updated.')
      } else {
        await create.mutateAsync(payload)
        toast.success('Unit created.')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, draft.id ? 'update this unit' : 'create this unit'))
    }
  }

  const handleDelete = async (unit: any) => {
    if (!confirm(`Delete "${unit.displayName}"? This can't be undone.`)) return
    setDeletingId(unit.id)
    try {
      await remove.mutateAsync(unit.id)
      toast.success('Unit deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this unit'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleToggleActive = async (unit: any) => {
    try {
      await setStatus.mutateAsync({ id: unit.id, isActive: !unit.isActive })
      toast.success(unit.isActive ? 'Unit deactivated.' : 'Unit activated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'toggle unit status'))
    }
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">Units</h2>
          <p className="text-sm text-muted-foreground">Manage units of measurement for products and services.</p>
        </div>
        <Button onClick={openCreate} className="gap-1.5">
          <Plus className="h-4 w-4" /> New Unit
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search units..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Active Filter */}
            <Select value={isActiveFilter} onValueChange={handleIsActiveChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : unitMasters.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No units yet. Create the first one.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {unitMasters.map((unit) => (
            <Card key={unit.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={unit.isActive ? 'default' : 'secondary'} className="text-xs">
                        {unit.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground truncate">{unit.displayName}</p>
                        <span className="text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                          {unit.shortCode}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {unit.name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(unit)} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleActive(unit)}
                      title={unit.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {unit.isActive ? (
                        <span className="h-3.5 w-3.5 rounded-full bg-green-500" />
                      ) : (
                        <span className="h-3.5 w-3.5 rounded-full bg-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(unit)}
                      disabled={deletingId === unit.id}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{draft.id ? 'Edit Unit' : 'New Unit'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="unit-name">Name *</Label>
                <Input
                  id="unit-name"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  placeholder="e.g. Numbers"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="unit-short-code">Short Code *</Label>
                <Input
                  id="unit-short-code"
                  value={draft.shortCode}
                  onChange={(e) => setDraft((d) => ({ ...d, shortCode: e.target.value.toUpperCase() }))}
                  placeholder="e.g. NOS"
                  maxLength={10}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="unit-display-name">Display Name *</Label>
              <Input
                id="unit-display-name"
                value={draft.displayName}
                onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value }))}
                placeholder="e.g. Nos"
              />
            </div>

            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))}
                />
                <span>Active</span>
              </Label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
              {draft.id ? 'Save Changes' : 'Create Unit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}